import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import {
  CodingStrategyEnum,
  drillFragmentData,
  getFragmentData,
  OrientationEnum,
  PaperFormatEnum,
} from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useEditableTrackableData } from '../../../hooks/useEditableTrackableData.hook';
import { useDeepMemo } from '../../../hooks/useDeepMemo.hook';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { findInitialWorkflowOfMissionWorkflow } from '../../../utils/workflows-of-mission-workflow.util';
import { ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment } from '../../../pages/ResolvableMissionType/QuotationHeader';
import { ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment } from '../../../pages/ResolvableMissionType/Tabs';
import type { MissionQuotationWithItsInfo } from '../../../pages/MissionQuotation/types/mission-quotation-with-its-info.type';

import { useInitialValuesFactory as useQuotationHeaderInitialValuesFactory } from '../../QuotationHeader/hooks/useInitialValuesFactory.hook';
import { MissionQuotationInfoStateForm_ResolvableMissionType_Fragment } from '../../MissionQuotationInfoState';

export const useInitialValuesFactory = (
  pInitialValues?: MissionQuotationWithItsInfo,
) => {
  const { generateId } = useIdFactory();

  const { trackableData } = useEditableTrackableData();

  const referencesContext = useReferencesContext();

  const {
    defaultGenericStatus,
    defaultQuotationWorkflow,
    defaultQuotationState,
  } = ensureDefined({
    defaultGenericStatus: referencesContext.defaultGenericStatus,
    defaultQuotationWorkflow: referencesContext.defaultQuotationWorkflow,
    defaultQuotationState: referencesContext.defaultQuotationState,
  });

  const {
    codingStrategiesCodeMap,
    contentOriginsCodeMap,
    documentTypesCodeMap,
    printStylesCodeMap,
  } = referencesContext;

  const now = useMemo(
    () =>
      new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Paris',
      }),
    [],
  );

  const { mission, resolvableMissionType: ppResolvableMissionType } =
    useMissionReadyContext();

  const initialWorkflowId = findInitialWorkflowOfMissionWorkflow(
    referencesContext.quotationWorkflows,
    defaultQuotationWorkflow,
    mission.workflowId,
  )._id;

  const resolvableMissionTypeQuotationHeader = drillFragmentData(
    [
      ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment,
      ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment,
    ],
    ppResolvableMissionType,
  );

  const missionTypeQuotationHeader =
    resolvableMissionTypeQuotationHeader?.quotationHeader;

  // A quotation being initialized takes the quotation template of the mission type.
  const missionTypeQuotationTemplateId = getFragmentData(
    MissionQuotationInfoStateForm_ResolvableMissionType_Fragment,
    ppResolvableMissionType,
  )?.quotationTemplateId;

  const headerInput = useDeepMemo(
    () =>
      omitBy(
        pInitialValues?.info.header ?? {
          __typename: 'QuotationHeader' as const,
          _id: generateId(),
          ...missionTypeQuotationHeader,
        },
        isNil,
      ),
    {
      header: pInitialValues?.info.header,
      fallback: missionTypeQuotationHeader,
    },
  );

  const { initialValues: headerInitialValues } =
    useQuotationHeaderInitialValuesFactory(headerInput);

  const initialValues: MissionQuotationWithItsInfo = useMemo(
    () => ({
      __typename: 'Quotation' as const,
      _id: generateId(),
      code: `DEVIS_${now}`,
      typeId: documentTypesCodeMap.Quotation._id,
      kind: documentTypesCodeMap.Quotation.code,
      statusId: defaultGenericStatus._id,
      title: mission.consolidation.title,
      ...trackableData,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        code: `DEVIS_${now}`,
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      virtualRoot: {
        __typename: 'Section' as const,
        _id: generateId(),
        isVirtual: true,
        contentOriginId: contentOriginsCodeMap.Special._id,
        subSections: EMPTY_ARRAY,
        orientation: OrientationEnum.Portrait,
        paperFormat: PaperFormatEnum.A4,
        hasPagebreakAfter: false,
        hasPagebreakBefore: false,
        ...omitBy(pInitialValues?.virtualRoot, isNil),
      },
      printing: {
        __typename: 'QuotationPrinting' as const,
        _id: generateId(),
        pdfPrints: EMPTY_ARRAY,
        pdfPrintsIncrement: 0,
        ...omitBy(pInitialValues?.printing, isNil),
        printSettings: {
          __typename: 'PrintSettings' as const,
          _id: generateId(),
          addFooters: true,
          addHeaders: true,
          printStyleId: printStylesCodeMap.Blank._id,
          ...omitBy(pInitialValues?.printing.printSettings, isNil),
        },
      },
      info: {
        __typename: 'QuotationInfo' as const,
        _id: generateId(),
        quotationId: EMPTY_ID,
        stateId: defaultQuotationState._id,
        workflowId: initialWorkflowId,
        ...omitBy(pInitialValues?.info, isNil),
        header: headerInitialValues,
      },
      // An existing quotation keeps its template, or none.
      templateId: pInitialValues
        ? (pInitialValues.templateId ?? EMPTY_ID)
        : (missionTypeQuotationTemplateId ?? EMPTY_ID),
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      contentOriginsCodeMap.Special._id,
      defaultGenericStatus._id,
      defaultQuotationState._id,
      documentTypesCodeMap.Quotation._id,
      documentTypesCodeMap.Quotation.code,
      generateId,
      headerInitialValues,
      initialWorkflowId,
      mission.consolidation.title,
      missionTypeQuotationTemplateId,
      now,
      pInitialValues,
      printStylesCodeMap.Blank._id,
      trackableData,
    ],
  );

  return { initialValues };
};
